import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, MicOff, Camera, Trash2, MapPin, Folder, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { compressImage } from '../utils/imageCompression';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SiteWalk = ({ user }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [location, setLocation] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    fetchNotes();
    initSpeechRecognition();
  }, []);

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscription(prev => prev + finalTranscript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech') {
          toast.error('Speech recognition error: ' + event.error);
          setIsRecording(false);
        }
      };

      recognitionRef.current.onend = () => {
        if (isRecording) {
          recognitionRef.current.start();
        }
      };
    } else {
      toast.error('Speech recognition not supported in this browser');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/site-walk`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes');
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not available');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped');
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.success('Recording started - speak your observations');
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsLoading(true);
    try {
      for (const file of files) {
        toast.info('Compressing image...');
        const compressedFile = await compressImage(file, 1280, 1280, 0.8);
        
        const formData = new FormData();
        formData.append('file', compressedFile);
        formData.append('description', 'Site walk photo');

        const response = await axios.post(`${API}/photos/upload-general`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setPhotos(prev => [...prev, response.data.url]);
      }
      toast.success(`${files.length} photo(s) added`);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!transcription.trim()) {
      toast.error('Please add some notes first');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/site-walk`, {
        project_id: (selectedProject && selectedProject !== 'none') ? selectedProject : null,
        transcription: transcription.trim(),
        location: location.trim() || null,
        photos: photos
      });

      toast.success('Site walk note saved!');
      setTranscription('');
      setLocation('');
      setPhotos([]);
      setSelectedProject('');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await axios.delete(`${API}/site-walk/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="page-container">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Site Walk</h1>
          <p className="text-gray-600">Walk around the worksite, take photos, and speak your observations</p>
        </div>

        {/* Recording Card */}
        <div className="glass-card p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-3">New Observation</h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <Button
                onClick={toggleRecording}
                className={`${isRecording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'} text-white w-full sm:w-auto min-h-[48px]`}
              >
                {isRecording ? <MicOff className="w-5 h-5 mr-2" /> : <Mic className="w-5 h-5 mr-2" />}
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                disabled={isLoading}
                className="w-full sm:w-auto min-h-[48px]"
              >
                <Camera className="w-5 h-5 mr-2" />
                Add Photos
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>
          </div>

          {/* Project Selection */}
          <div className="mb-4">
            <Label className="text-gray-700 mb-2 block">
              <Folder className="w-4 h-4 inline mr-2" />
              Link to Project (Optional)
            </Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div className="mb-4">
            <Label className="text-gray-700 mb-2 block">
              <MapPin className="w-4 h-4 inline mr-2" />
              Location/Area (Optional)
            </Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., North wall, Kitchen, Second floor"
            />
          </div>

          {/* Transcription */}
          <div className="mb-4">
            <Label className="text-gray-700 mb-2 block">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Notes {isRecording && <span className="text-red-600 animate-pulse">(Recording...)</span>}
            </Label>
            <textarea
              value={transcription}
              onChange={(e) => setTranscription(e.target.value)}
              placeholder="Speak your observations or type here..."
              className="w-full min-h-[120px] p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Photo Preview */}
          {photos.length > 0 && (
            <div className="mb-4">
              <Label className="text-gray-700 mb-2 block">Photos ({photos.length})</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative">
                    <img
                      src={`${BACKEND_URL}${photo}`}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded"
                    />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSaveNote}
            disabled={isLoading || !transcription.trim()}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold min-h-[44px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Observation'
            )}
          </Button>
        </div>

        {/* Notes Timeline */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Previous Observations</h2>
          
          {notes.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No observations yet. Start your first site walk!</p>
          ) : (
            <div className="space-y-4">
              {notes.map(note => (
                <div key={note.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{note.created_by_name}</p>
                      <p className="text-sm text-gray-500">{new Date(note.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}</p>
                    </div>
                    {((user.role === 'admin' || user.role === 'supervisor') || user.id === note.created_by) && (
                      <Button
                        onClick={() => handleDeleteNote(note.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 w-full sm:w-auto min-h-[40px]"
                      >
                        <Trash2 className="w-4 h-4 mr-2 sm:mr-0" />
                        <span className="sm:hidden">Delete</span>
                      </Button>
                    )}
                  </div>

                  {note.project_name && (
                    <p className="text-sm text-blue-600 mb-2">
                      <Folder className="w-4 h-4 inline mr-1" />
                      {note.project_name}
                    </p>
                  )}

                  {note.location && (
                    <p className="text-sm text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {note.location}
                    </p>
                  )}

                  <p className="text-gray-800 whitespace-pre-wrap mb-3">{note.transcription}</p>

                  {note.photos && note.photos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {note.photos.map((photo, index) => (
                        <img
                          key={index}
                          src={`${BACKEND_URL}${photo}`}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteWalk;
